import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import { useState } from "react";
import TextField from "@components/ui/textfields/TextField";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import DatePicker from "@components/ui/selection/DatePicker";
import DropDown from "@components/ui/dropdown/DropDown";
import DropDownMultiple from "@components/ui/dropdown/DropDownMultiple";
import { Broadcast, PROJECT_STATUS_OPTIONS, ProjectStatus } from "types";
import { showNotification } from "context/notification.service";
import { useProjectStore } from "@store/projectStore";
import { getFlutterTime, getFlutterFromSeconds } from "@services/timeHelpers";

const startOfDay = (seconds: number): number => {
  const d = new Date(seconds * 1000);
  d.setHours(0, 0, 0, 0);
  return Math.floor(d.getTime() / 1000);
};

const endOfDay = (seconds: number): number => {
  const d = new Date(seconds * 1000);
  d.setHours(23, 59, 59, 999);
  return Math.floor(d.getTime() / 1000);
};

export const ModifyBroadcastModal = ({
  onClose,
  initialBroadcast,
}: {
  onClose: () => void;
  initialBroadcast: Broadcast;
}) => {
  const updateBroadcast = useProjectStore((state) => state.updateBroadcast);
  const removeBroadcasts = useProjectStore((state) => state.removeBroadcasts);

  const [title, setTitle] = useState(initialBroadcast.title);
  const [content, setContent] = useState(initialBroadcast.content);
  const [status, setStatus] = useState<"active" | "inactive">(
    initialBroadcast.status,
  );
  const [startDateSeconds, setStartDateSeconds] = useState(
    initialBroadcast.start_date.seconds,
  );
  const [endDateSeconds, setEndDateSeconds] = useState(
    initialBroadcast.end_date.seconds,
  );
  const [projectVisibility, setProjectVisibility] = useState<ProjectStatus[]>(
    initialBroadcast.project_visibility ?? PROJECT_STATUS_OPTIONS.map((o) => o.value),
  );

  function handleSave() {
    if (!title.trim()) {
      showNotification({
        type: "error",
        message: "Le titre est obligatoire",
      });
      return;
    }

    const updated: Broadcast = {
      ...initialBroadcast,
      title: title.trim(),
      content,
      status,
      project_visibility: projectVisibility,
      start_date: getFlutterFromSeconds(startOfDay(startDateSeconds)),
      end_date: getFlutterFromSeconds(endOfDay(endDateSeconds)),
      updated_at: getFlutterTime(),
    };

    updateBroadcast(updated);
    showNotification({
      type: "success",
      message: "Annonce modifiée avec succès",
    });
    onClose();
  }

  return (
    <BaseModal
      title="Modifier l'annonce"
      startIcon={<Icon1 name="broadcast" size={20} />}
      onClose={onClose}
    >
      <div className="bg-white flex flex-col gap-2 p-4">
        <div className="flex flex-row gap-2">
          <DatePicker
            label="Date de début (00:01)"
            seconds={startDateSeconds}
            onChange={(seconds) => setStartDateSeconds(seconds)}
          />
          <DatePicker
            label="Date de fin (23:59)"
            seconds={endDateSeconds}
            onChange={(seconds) => setEndDateSeconds(seconds)}
            minDateSeconds={startDateSeconds}
          />
        </div>
        <div className="flex flex-row gap-2">
          <TextField
            label="Titre"
            placeholder="Vacances de Noël"
            mandatory={true}
            value={title}
            onChange={(value) => setTitle(value)}
          />
          <TextField
            label="Auteur"
            value={initialBroadcast.author.display_name}
            disabled={true}
          />
        </div>
        <AreaTextField
          label="Contenu"
          placeholder="Rédigez votre annonce ici..."
          value={content}
          onChange={(value) => setContent(value)}
          rows={8}
        />
        <div className="flex flex-row gap-2">
          <DropDown
            options={[
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
            ]}
            label="État"
            value={status}
            onSelect={(value) => setStatus(value as "active" | "inactive")}
          />
          <DropDownMultiple
            label="Visibilité dans les projets"
            options={PROJECT_STATUS_OPTIONS}
            value={projectVisibility}
            onSelect={(values) =>
              setProjectVisibility(values as ProjectStatus[])
            }
          />
        </div>
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-between">
        <SecondaryButton
          variant="small"
          danger={true}
          onClick={() => {
            removeBroadcasts([initialBroadcast.id]);
            showNotification({
              type: "success",
              message: "Annonce supprimée",
            });
            onClose();
          }}
        >
          Supprimer
        </SecondaryButton>
        <div className="flex flex-row gap-2">
          <SecondaryButton variant="small" onClick={onClose}>
            Annuler
          </SecondaryButton>
          <Button
            variant="small"
            label="Enregistrer"
            onClick={handleSave}
          />
        </div>
      </div>
    </BaseModal>
  );
};
